import { API_BASE_URL } from '../config/apiConfig'
import Thumbnail, { resolveThumbnailPath, resolveThumbnailPathFromCategory, ThumbnailCategory, ThumbnailStatus, WithThumbnail } from './thumbnail';
import axios from 'axios';

export default class DjehootyFile implements WithThumbnail {
    id: number;
    rank: number;
    uuid: string;
    filename: string;
    filename_orig: string;
    size: number;
    media_type: string;
    sha256: string;
    thumbnail: Thumbnail;
    path: string;
    importedAt: number;
    modifiedAt: number;
    user_imported: number;

    constructor(
        id: number = 0,
        rank: number = 0,
        uuid: string = "",
        media_type: string = "",
        sha256: string = "",
        size: number = 0,
        filename: string = "",
        filename_orig: string = "",
        thumbnail: Thumbnail = new Thumbnail(),
        path: string = "",
        importedAt: number = 0,
        modifiedAt: number = 0,
        user_imported: number = 0
    ) {
        this.id = id;
        this.uuid = uuid;
        this.media_type = media_type;
        this.sha256 = sha256;
        this.size = size;
        this.filename = filename;
        this.filename_orig = filename_orig;
        this.importedAt = importedAt;
        this.modifiedAt = modifiedAt;
        this.thumbnail = thumbnail;
        this.path = path;
        this.rank = rank;
        this.user_imported = user_imported;
    }

    // TODO : it seems a bit dirty to me, to be checked
    loadFromJson(jsonPayload: { id: number; uuid: string; media_type: string; sha256: string; size: number; filename: string; filename_orig: string; imported_at: number; modified_at: number; thumbnail: Thumbnail; path: string; rank: number; user_imported: number; }) {
        this.id = jsonPayload.id;
        this.uuid = jsonPayload.uuid;
        this.media_type = jsonPayload.media_type;
        this.sha256 = jsonPayload.sha256;
        this.size = jsonPayload.size;
        this.filename = jsonPayload.filename;
        this.filename_orig = jsonPayload.filename_orig;
        this.importedAt = jsonPayload.imported_at;
        this.modifiedAt = jsonPayload.modified_at;
        if (jsonPayload.thumbnail) {
            this.thumbnail = new Thumbnail(jsonPayload.thumbnail.file_thumbnail_id, jsonPayload.thumbnail.url, jsonPayload.thumbnail.status, jsonPayload.thumbnail.category);
        }
        this.path = jsonPayload.path;
        this.rank = jsonPayload.rank;
        this.user_imported = jsonPayload.user_imported;
    }

    /* Return a kB, mB, gB value */
    getSizeHumanReadable = (): string => {
        let result = "";
        let kBsize = this.size / 1024;

        if (kBsize > 1024) {
            let MBsize = kBsize / 1024;
            if (MBsize > 1024) {
                let GBsize = MBsize / 1024;
                result = GBsize.toPrecision(3) + "GB";
            } else {
                result = MBsize.toPrecision(3) + "MB";
            }
        } else {
            result = kBsize.toPrecision(3) + "kB";
        }
        return result;
    }

    getTruncatedSha256 = (): string => {
        return this.sha256.slice(0, 12);
    }

    /**
     * Gets the thumbnail status of the file
     * @returns ThumbnailStatus
     */
    getThumbnailStatus = (): ThumbnailStatus => {
        if (!this.thumbnail) {
            return ThumbnailStatus.None;
        } else {
            return this.thumbnail.status;
        }
    }

    /**
     * Gets the thumbnail path of the file
     * @returns string - The thumbnail path
     */
    getThumbnailPath = (): string => {
        // Missing thumbnail structure. It should not happen, but in this case we return a default thumbnail for unknown media type
        if (!this.thumbnail) {
            return resolveThumbnailPath(ThumbnailStatus.None, "", ThumbnailCategory.Other);
        }

        return this.thumbnail.getPath();
    }

    getPath = (): string => {
        if (!this.thumbnail) {
            return "";
        } else {
            return API_BASE_URL + this.thumbnail.url;
        }
    }

    getDateModifiedHumanReadable = (): string => {
        const date = new Date(this.modifiedAt * 1000);
        const formattedDate = date.toLocaleDateString("fr-FR", {
            year: "numeric",
            month: "numeric",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
        return formattedDate;
    }

    getDateImportedHumanReadable = (): string => {
        const date = new Date(this.importedAt * 1000);
        const formattedDate = date.toLocaleDateString("fr-FR", {
            year: "numeric",
            month: "numeric",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
        return formattedDate;
    }

    /**
     * Import a file from a given URL
     * @param url - The URL of the file to import
     * @returns - void
     */
    static async importFromURL(url: string): Promise<void> {
        if (!url) {
            throw new Error("URL is empty");
        }
        try {
            const apiData = { urls: [url] };
            await axios.post(API_BASE_URL + '/files', apiData, {
                headers: {
                    'Accept': 'application/json',
                },
                validateStatus: (status) => status === 201,
            });
            return;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Upload files to the server
     * @param files - Array of File objects to upload
     * @returns - void
     */
    static async uploadFiles(files: File[]): Promise<void> {
        if (files.length === 0) {
            throw new Error("No file to upload");
        }

        const formData = new FormData()
        files.forEach((file) => {
            formData.append('files', file)
        })

        try {
            await axios.post(API_BASE_URL + '/files', formData, {
                headers: {
                    'Accept': 'application/json',
                },
                validateStatus: (status) => status === 201,
            });
            return;
        } catch (error: any) {
            if (error.response) {
                console.log("API Response Error:", error.response.data);
            } else if (error.request) {
                console.log("No response received:", error.request);
            } else {
                console.log("An unexpected error occurred:", error.message);
            }
            throw error;
        }
    }
}